// Foreground-only proximity helpers for the Safety Check-In "you're near a
// saved address" nudge. Deliberately NOT background geofencing: we take a
// single location reading when the app is open, which needs only foreground
// location permission and avoids the battery cost + app-store scrutiny of
// always-on background location. See BACKLOG.md / the ADR for that tradeoff.
//
// On native it uses @capacitor/geolocation, which prompts for and reads the OS
// location (the WebView's navigator.geolocation is unreliable in the native
// shell - iOS WKWebView doesn't bridge it to the OS at all). On web it falls
// back to navigator.geolocation. Same { lat, lng } | null contract either way.
import { Capacitor } from "@capacitor/core";
import { Geolocation } from "@capacitor/geolocation";

const DEFAULT_RADIUS_M = 150;

// Great-circle distance between two lat/lng points, in metres (haversine).
export function distanceMeters(lat1, lng1, lat2, lng2) {
  const R = 6371000; // Earth radius in metres
  const toRad = (d) => (d * Math.PI) / 180;
  const dLat = toRad(lat2 - lat1);
  const dLng = toRad(lng2 - lng1);
  const a =
    Math.sin(dLat / 2) ** 2 +
    Math.cos(toRad(lat1)) * Math.cos(toRad(lat2)) * Math.sin(dLng / 2) ** 2;
  return 2 * R * Math.asin(Math.sqrt(a));
}

// Resolves the current position, or null if unavailable/denied/timed out.
// Never rejects - callers treat null as "couldn't locate" and fall back to
// manual selection.
//
// Pass { highAccuracy: true } when the coordinate needs to be precise (the "use
// my current location" buttons, which reverse-geocode to a street address): it
// uses the real GPS and refuses a stale cached fix, at the cost of a little more
// time/battery. The default (low accuracy, cached ok) suits the quick proximity
// nudge.
export async function getCurrentPosition({ highAccuracy = false } = {}) {
  const options = {
    enableHighAccuracy: highAccuracy,
    timeout: highAccuracy ? 15000 : 8000,
    maximumAge: highAccuracy ? 0 : 60000,
  };

  if (Capacitor.isNativePlatform()) {
    try {
      let perm = await Geolocation.checkPermissions();
      if (
        perm.location === "prompt" ||
        perm.location === "prompt-with-rationale"
      ) {
        perm = await Geolocation.requestPermissions();
      }
      if (perm.location !== "granted" && perm.coarseLocation !== "granted") {
        return null;
      }
      const pos = await Geolocation.getCurrentPosition(options);
      return {
        lat: pos.coords.latitude,
        lng: pos.coords.longitude,
        accuracy: pos.coords.accuracy, // radius in metres (may be undefined)
      };
    } catch (err) {
      console.warn("Native geolocation unavailable:", err?.message);
      return null;
    }
  }

  // Web fallback.
  return new Promise((resolve) => {
    if (!("geolocation" in navigator)) {
      resolve(null);
      return;
    }
    navigator.geolocation.getCurrentPosition(
      (pos) =>
        resolve({
          lat: pos.coords.latitude,
          lng: pos.coords.longitude,
          accuracy: pos.coords.accuracy,
        }),
      (err) => {
        console.warn("Geolocation unavailable:", err?.message);
        resolve(null);
      },
      options,
    );
  });
}

// Returns the nearest saved address within `radiusM` of `coords`, or null.
// Ignores addresses without stored coordinates (added before lat/lng capture).
export function findNearbyAddress(addresses, coords, radiusM = DEFAULT_RADIUS_M) {
  if (!coords) return null;
  let best = null;
  let bestDist = Infinity;
  for (const a of addresses) {
    if (typeof a.lat !== "number" || typeof a.lng !== "number") continue;
    const d = distanceMeters(coords.lat, coords.lng, a.lat, a.lng);
    if (d <= radiusM && d < bestDist) {
      best = a;
      bestDist = d;
    }
  }
  return best;
}
